import { useEffect, useRef } from "react";

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  pulsePhase: number;
  pulseSpeed: number;
}

interface WindowExt {
  __PRERENDER__?: boolean;
  matchMedia: Window["matchMedia"];
  setTimeout: Window["setTimeout"];
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (h: number) => void;
}


export function NeuralNetworkBg() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const nodesRef = useRef<Node[]>([]);
  const timeRef = useRef(0);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const getParent = () => canvas.parentElement || document.body;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const parent = getParent();
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const initNodes = () => {
      const parent = getParent();
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      const cols = Math.ceil(w / 120);
      const rows = Math.ceil(h / 120);
      const nodes: Node[] = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const cellW = w / cols;
          const cellH = h / rows;
          nodes.push({
            x: cellW * c + cellW * 0.5 + (Math.random() - 0.5) * cellW * 0.7,
            y: cellH * r + cellH * 0.5 + (Math.random() - 0.5) * cellH * 0.7,
            vx: (Math.random() - 0.5) * 0.2,
            vy: (Math.random() - 0.5) * 0.2,
            radius: 1.5 + Math.random() * 1.2,
            pulsePhase: Math.random() * Math.PI * 2,
            pulseSpeed: 0.006 + Math.random() * 0.01,
          });
        }
      }
      nodesRef.current = nodes;
    };

    const getColor = () => {
      const root = getComputedStyle(document.documentElement);
      const raw = root.getPropertyValue("--primary").trim() || "190 79% 29%";
      const parts = raw.split(/\s+/);
      return { h: parts[0], s: parts[1], l: parts[2] };
    };

    // Pre-compute neighbor pairs once (not every frame)
    let neighborPairs: [number, number][] = [];
    
    const computeNeighbors = () => {
      const nodes = nodesRef.current;
      const pairs: [number, number][] = [];
      for (let i = 0; i < nodes.length; i++) {
        const dists: { j: number; dist: number }[] = [];
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          dists.push({ j, dist: Math.sqrt(dx * dx + dy * dy) });
        }
        dists.sort((a, b) => a.dist - b.dist);
        for (let k = 0; k < Math.min(3, dists.length); k++) {
          pairs.push([i, dists[k].j]);
        }
      }
      neighborPairs = pairs;
    };

    let frameCount = 0;

    // Cap the animation to ~30fps. The neural mesh reads identically to the eye
    // at half the frame rate but roughly halves the main-thread work — the main
    // driver behind Lighthouse's Speed Index / TBT penalty for this hero.
    const FRAME_INTERVAL = 1000 / 30;
    let lastFrame = 0;

    const draw = (now = 0) => {
      animRef.current = requestAnimationFrame(draw);
      if (now - lastFrame < FRAME_INTERVAL) return;
      lastFrame = now;
      renderFrame();
    };

    const renderFrame = () => {
      const parent = getParent();
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      const nodes = nodesRef.current;
      const c = getColor();
      const hsla = (a: number) => `hsla(${c.h}, ${c.s}, ${c.l}, ${a})`;
      timeRef.current += 1;
      frameCount++;

      ctx.clearRect(0, 0, w, h);

      const mouse = mouseRef.current;
      const mRadius = 160;

      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;

        // Gentle interaction with the cursor: nodes drift toward it, close ones get nudged away
        if (mouse.active) {
          const mdx = n.x - mouse.x;
          const mdy = n.y - mouse.y;
          const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
          if (mdist < mRadius && mdist > 0.1) {
            const force = (1 - mdist / mRadius) * 0.6;
            n.x += (mdx / mdist) * force;
            n.y += (mdy / mdist) * force;
          }
        }

        if (n.x < 10 || n.x > w - 10) n.vx *= -1;
        if (n.y < 10 || n.y > h - 10) n.vy *= -1;
        n.x = Math.max(5, Math.min(w - 5, n.x));
        n.y = Math.max(5, Math.min(h - 5, n.y));
      }

      // Recompute neighbors every 120 frames (~2s)
      if (frameCount % 120 === 1) computeNeighbors();

      // Draw connections
      for (const [i, j] of neighborPairs) {
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const alpha = Math.max(0.15, (1 - dist / 250) * 0.55);

        ctx.beginPath();
        ctx.moveTo(nodes[i].x, nodes[i].y);
        ctx.lineTo(nodes[j].x, nodes[j].y);
        ctx.strokeStyle = hsla(alpha);
        ctx.lineWidth = 1.2;
        ctx.stroke();

        if ((i + j) % 5 === 0) {
          const t = ((timeRef.current * 0.002 + i * 0.07) % 1);
          const sx = nodes[i].x + (nodes[j].x - nodes[i].x) * t;
          const sy = nodes[i].y + (nodes[j].y - nodes[i].y) * t;
          const sigAlpha = alpha * 4 * Math.sin(t * Math.PI);
          ctx.beginPath();
          ctx.arc(sx, sy, 2, 0, Math.PI * 2);
          ctx.fillStyle = hsla(Math.min(sigAlpha, 0.7));
          ctx.fill();
        }
      }

      for (const n of nodes) {
        const pulse = Math.sin(timeRef.current * n.pulseSpeed + n.pulsePhase);
        const r = n.radius + pulse * 0.3;
        const alpha = 0.55 + pulse * 0.15;

        ctx.beginPath();
        ctx.arc(n.x, n.y, r * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = hsla(alpha * 0.3);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fillStyle = hsla(alpha);
        ctx.fill();
      }

      // Draw glowing links from the cursor to nearby nodes
      if (mouse.active) {
        for (const n of nodes) {
          const dx = n.x - mouse.x;
          const dy = n.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < mRadius) {
            const alpha = (1 - dist / mRadius) * 0.6;
            ctx.beginPath();
            ctx.moveTo(mouse.x, mouse.y);
            ctx.lineTo(n.x, n.y);
            ctx.strokeStyle = hsla(alpha);
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
        // Cursor glow
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = hsla(0.8);
        ctx.fill();
      }
    };

    resize();
    initNodes();
    computeNeighbors();

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const win = window as unknown as WindowExt;
    const isPrerender = typeof window !== "undefined" && !!win.__PRERENDER__;
    // Mobile / touch devices: the animation loop is the single biggest driver of
    // this page's mobile Speed Index (the above-the-fold canvas never stops
    // repainting on a throttled phone) and there's no cursor to interact with.
    // Draw one static frame instead — visually identical, main thread free.
    const isMobile =
      typeof window !== "undefined" &&
      (window.matchMedia?.("(max-width: 767px)").matches ||
        window.matchMedia?.("(pointer: coarse)").matches);

    let startTimer: number | undefined;
    let idleHandle: number | undefined;

    if (prefersReduced || isPrerender || isMobile) {
      // Accessibility + prerender + mobile: draw a single static frame so the hero
      // still shows the mesh, but the main thread stays completely free.
      renderFrame();
    } else {
      // Defer the animation loop until the browser is idle so it never competes
      // with hydration and first paint — this is what keeps TBT low.
      const start = () => draw();
      if (typeof win.requestIdleCallback === "function") {
        idleHandle = win.requestIdleCallback(start, { timeout: 1500 });
      } else {
        startTimer = window.setTimeout(start, 600);
      }
    }

    const onResize = () => {
      resize();
      initNodes();
      computeNeighbors();
      if (prefersReduced || isPrerender || isMobile) renderFrame();
    };
    window.addEventListener("resize", onResize);

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.active = true;
    };
    const onMouseLeave = () => {
      mouseRef.current.active = false;
    };
    const parentEl = getParent();
    parentEl.addEventListener("mousemove", onMouseMove);
    parentEl.addEventListener("mouseleave", onMouseLeave);

    return () => {
      cancelAnimationFrame(animRef.current);
      if (startTimer !== undefined) clearTimeout(startTimer);
      if (idleHandle !== undefined && typeof win.cancelIdleCallback === "function") {
        win.cancelIdleCallback(idleHandle);
      }
      window.removeEventListener("resize", onResize);
      parentEl.removeEventListener("mousemove", onMouseMove);
      parentEl.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: 0.85 }}
    />
  );
}
